import { motion } from 'framer-motion';
import { Shield, Mail, MapPin } from 'lucide-react';

export function PoliticaPrivacidade() {
  return (
    <div className="min-h-screen bg-gray-950 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-orange-500/10 flex items-center justify-center">
            <Shield className="w-8 h-8 text-orange-500" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Política de Privacidade
          </h1>
          <p className="text-gray-400">
            Última atualização: {new Date().toLocaleDateString('pt-BR')}
          </p>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="prose prose-invert prose-orange max-w-none"
        >
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">1. Introdução</h2>
            <p className="text-gray-300 leading-relaxed">
              A Fort3D Indústria e Comércio LTDA, CNPJ 00.000.000/0000-00, com sede em São Paulo/SP, 
              valoriza a privacidade de seus usuários e está comprometida em proteger suas informações 
              pessoais. Esta Política de Privacidade descreve como coletamos, usamos, armazenamos e 
              protegemos seus dados quando você utiliza nosso site, blog e serviços.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">2. Informações que Coletamos</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Podemos coletar as seguintes informações:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2">
              <li><strong>Informações pessoais:</strong> nome, e-mail, telefone, endereço, CPF/CNPJ fornecidos voluntariamente;</li>
              <li><strong>Informações de navegação:</strong> endereço IP, tipo de navegador, páginas visitadas, tempo de permanência;</li>
              <li><strong>Cookies e tecnologias similares:</strong> para melhorar sua experiência e personalizar conteúdo;</li>
              <li><strong>Dados de compra:</strong> histórico de pedidos, preferências de produtos.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">3. Como Usamos Suas Informações</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Utilizamos suas informações para:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2">
              <li>Processar e entregar seus pedidos;</li>
              <li>Comunicar-nos sobre seus pedidos e conta;</li>
              <li>Personalizar sua experiência no site;</li>
              <li>Enviar newsletters e conteúdo relevante (com seu consentimento);</li>
              <li>Melhorar nossos produtos e serviços;</li>
              <li>Cumprir obrigações legais e regulatórias;</li>
              <li>Exibir anúncios personalizados (Google AdSense).</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">4. Cookies e Tecnologias de Rastreamento</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Utilizamos cookies e tecnologias similares para:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2">
              <li><strong>Cookies essenciais:</strong> necessários para o funcionamento do site;</li>
              <li><strong>Cookies de preferências:</strong> lembram suas configurações;</li>
              <li><strong>Cookies de análise:</strong> Google Analytics para entender como você usa o site;</li>
              <li><strong>Cookies de publicidade:</strong> Google AdSense para exibir anúncios relevantes.</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-4">
              Você pode gerenciar suas preferências de cookies através do banner de consentimento 
              ou nas configurações do seu navegador.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">5. Google AdSense</h2>
            <p className="text-gray-300 leading-relaxed">
              Utilizamos o Google AdSense para exibir anúncios em nosso blog. O Google utiliza 
              cookies para personalizar os anúncios exibidos com base em suas visitas anteriores 
              ao nosso site e outros sites na internet. Você pode desativar a personalização de 
              anúncios visitando as{' '}
              <a 
                href="https://adssettings.google.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-orange-400 hover:underline"
              >
                Configurações de Anúncios do Google
              </a>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">6. Compartilhamento de Informações</h2>
            <p className="text-gray-300 leading-relaxed">
              Não vendemos suas informações pessoais. Podemos compartilhar dados com:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2 mt-4">
              <li>Prestadores de serviços (logística, pagamento);</li>
              <li>Parceiros de publicidade (Google);</li>
              <li>Autoridades competentes quando legalmente obrigatório.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">7. Segurança dos Dados</h2>
            <p className="text-gray-300 leading-relaxed">
              Implementamos medidas de segurança técnicas e organizacionais para proteger suas 
              informações contra acesso não autorizado, alteração, divulgação ou destruição. 
              Utilizamos criptografia SSL/TLS para transmissão de dados sensíveis.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">8. Seus Direitos</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              De acordo com a LGPD, você tem os seguintes direitos:
            </p>
            <ul className="list-disc pl-6 text-gray-300 space-y-2">
              <li>Acessar seus dados pessoais;</li>
              <li>Corrigir dados incompletos ou desatualizados;</li>
              <li>Solicitar a exclusão de seus dados;</li>
              <li>Revogar consentimento;</li>
              <li>Solicitar portabilidade dos dados;</li>
              <li>Opor-se ao processamento de dados.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">9. Contato</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Para exercer seus direitos ou esclarecer dúvidas sobre esta política, entre em contato:
            </p>
            <div className="bg-gray-900 rounded-xl p-6 border border-white/5">
              <div className="flex items-center gap-3 mb-3">
                <Mail className="w-5 h-5 text-orange-500" />
                <span className="text-gray-300">privacidade@fort3d.com.br</span>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-orange-500" />
                <span className="text-gray-300">São Paulo, SP - Brasil</span>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">10. Alterações nesta Política</h2>
            <p className="text-gray-300 leading-relaxed">
              Podemos atualizar esta política periodicamente. A data da última atualização será 
              sempre indicada no topo da página. Recomendamos revisar esta página regularmente 
              para estar ciente de quaisquer alterações.
            </p>
          </section>
        </motion.div>
      </div>
    </div>
  );
}
